import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Menu, MessageCircle, Smartphone, Sparkles } from "lucide-react";
import { useStore } from "../../stores/rootStore";
import { muhahaNowHomeHref } from "../../lib/muhahaNow";
import { MobileQrCode, mobilePlayUrl } from "../mobile/MobileQrCode";
import { BrandLogo } from "./BrandLogo";
import { assetUrl } from "../../lib/assets";
import { UserMenu } from "./UserMenu";
import { HeaderSearch } from "./HeaderSearch";

export const TopBar = observer(() => {
	const { auth, dm, ui, notification } = useStore();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const discoveryPage = pathname === "/" || pathname.startsWith("/t/") || pathname.replace(/\/+$/, "") === "/all/tags";
	useEffect(() => ui.loadCataloguePreferences(), [ui]);
	const { t } = useTranslation();
	const unread = notification.unread + dm.unreadCount;
	const [mobileQrOpen, setMobileQrOpen] = useState(false);
	const [isScrolled, setIsScrolled] = useState(false);
	const mobileQrRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!auth.initialized) void auth.bootstrap();
	}, [auth]);

	useEffect(() => {
		const updateHeader = () => setIsScrolled(window.scrollY > 16);
		updateHeader();
		window.addEventListener("scroll", updateHeader, { passive: true });
		return () => window.removeEventListener("scroll", updateHeader);
	}, []);

	useEffect(() => {
		if (!mobileQrOpen) return;

		function handlePointerDown(event: PointerEvent) {
			if (!mobileQrRef.current?.contains(event.target as Node)) {
				setMobileQrOpen(false);
			}
		}

		function handleKeyDown(event: KeyboardEvent) {
			if (event.key === "Escape") setMobileQrOpen(false);
		}

		document.addEventListener("pointerdown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("pointerdown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [mobileQrOpen]);

	async function openCreate() {
		if (!auth.isLoggedIn) {
			const signedIn = await ui.requestAuth();
			if (!signedIn) return;
		}
		window.location.assign(muhahaNowHomeHref() ?? "/create");
	}

	return (
		<header
			className={`muhaha-topbar sticky top-0 z-30 transition-[background-color,border-color,box-shadow] duration-200 ${
				isScrolled
					? "border-b border-white/[0.08] bg-black/65 shadow-[0_12px_30px_rgba(0,0,0,0.16)] backdrop-blur-xl"
					: "border-b border-transparent bg-black"
			}`}
		>
			<div className="flex h-[52px] w-full items-center gap-2 px-4 sm:px-5 lg:px-6">
				{discoveryPage ? <>
					<button type="button" className="discovery-mobile-toggle" aria-controls="discovery-sidebar" aria-expanded={ui.discoveryMobileOpen}
						aria-label={t(ui.discoveryMobileOpen ? "discover.collapseSidebar" : "discover.expandSidebar")}
						onClick={() => ui.setDiscoveryMobileOpen(!ui.discoveryMobileOpen)}><Menu size={20} /></button>
				</> : null}
				<button
					type="button"
					onClick={() => navigate("/")}
					aria-label="Muhaha"
					className="flex shrink-0 items-center gap-1.5 transition hover:opacity-80"
				>
					<BrandLogo compact />
				</button>

				{pathname === "/" ? <HeaderSearch /> : <div className="min-w-0 flex-1" />}

				<button
					type="button"
					onClick={() => void openCreate()}
					className="topbar-create-button brand-gradient inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg px-3.5 text-xs font-semibold text-white shadow-[0_6px_18px_-10px_rgba(139,92,246,0.9)] transition hover:brightness-110"
				>
					<Sparkles size={14} className="hidden min-[380px]:block" />
					{t("nav.create")}
				</button>

				{/* A QR is useless on the phone itself, so the entry stays desktop-only. */}
				<div ref={mobileQrRef} className="relative hidden md:block">
					<button
						type="button"
						onClick={() => setMobileQrOpen((open) => !open)}
						aria-expanded={mobileQrOpen}
						aria-haspopup="dialog"
						aria-controls="topbar-mobile-qr"
						className="topbar-control topbar-mobile-button flex h-8 items-center gap-1.5 rounded-lg border border-white/12 bg-white/[0.07] px-3 text-xs font-semibold text-white/85 transition hover:border-white/20 hover:bg-white/[0.11] hover:text-white"
					>
						<Smartphone size={14} />
						{t("layout.playOnMobile")}
					</button>

					{mobileQrOpen && (
						<div
							id="topbar-mobile-qr"
							role="dialog"
							aria-label={t("mobilePlay.title")}
							className="topbar-mobile-qr absolute right-0 top-full z-30 mt-2 w-[216px] rounded-lg border border-white/15 bg-[#121016] p-3 text-center shadow-[0_16px_48px_rgba(0,0,0,0.55)]"
						>
							<MobileQrCode
								url={mobilePlayUrl()}
								size={192}
								alt={t("mobilePlay.qrAlt")}
								centerLogoSrc={assetUrl("icon-192.png")}
								className="rounded-md"
							/>
							<p className="mt-2 text-xs leading-relaxed text-muted">
								{t("mobilePlay.description")}
							</p>
						</div>
					)}
				</div>

				<button
					type="button"
					onClick={() => (auth.isLoggedIn ? navigate("/messages?tab=messages") : ui.openAuth())}
					aria-label={t("nav.messages")}
					title={t("nav.messages")}
					className="topbar-control topbar-message-button relative grid size-8 place-items-center rounded-lg border border-transparent bg-white/[0.06] text-white/55 transition hover:border-white/12 hover:bg-white/[0.1] hover:text-white"
				>
					<MessageCircle size={17} />
					{unread > 0 && (
						<span className="message-unread-badge absolute -right-1 -top-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-danger px-1 text-[11px] font-bold leading-none tabular-nums text-on-accent">
							{unread > 99 ? "99+" : unread}
						</span>
					)}
				</button>

				<UserMenu />
			</div>
		</header>
	);
});
